/** Strip Markdown/HTML from speaker script for word count, export, and teleprompter plain fallback. */
export function stripSpeakerScriptPlain(text) {
    let s = text.trim();
    if (!s)
        return "";
    s = s.replace(/```[\s\S]*?```/g, " ");
    s = s.replace(/`([^`]+)`/g, "$1");
    s = s.replace(/<[^>]+>/g, " ");
    s = s.replace(/^#{1,6}\s+/gm, "");
    s = s.replace(/\*\*([^*]+)\*\*/g, "$1");
    s = s.replace(/\*([^*]+)\*/g, "$1");
    s = s.replace(/__([^_]+)__/g, "$1");
    s = s.replace(/_([^_]+)_/g, "$1");
    s = s.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
    s = s.replace(/^\s*[-*+]\s+/gm, "");
    s = s.replace(/^\s*\d+\.\s+/gm, "");
    s = s.replace(/\s+/g, " ").trim();
    return s;
}
function normalizeNoteMarkdown(text) {
    return text
        .replace(/\r\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
function stringField(record, keys) {
    for (const key of keys) {
        const v = record[key];
        if (typeof v === "string" && v.trim())
            return v.trim();
    }
    return "";
}
function jsonObjectToNoteMarkdown(obj, depth = 0) {
    const lines = [];
    const title = stringField(obj, ["title", "name", "heading", "label"]);
    const body = stringField(obj, [
        "body",
        "text",
        "content",
        "answer",
        "summary",
        "snippet",
        "description",
        "explanation",
    ]);
    if (title)
        lines.push(depth === 0 ? `### ${title}` : `**${title}**`);
    if (body)
        lines.push(body);
    for (const [key, value] of Object.entries(obj)) {
        if (["title", "name", "heading", "label", "body", "text", "content", "answer", "summary", "snippet", "description", "explanation"].includes(key)) {
            continue;
        }
        if (value == null)
            continue;
        if (Array.isArray(value)) {
            const block = jsonArrayToNoteMarkdown(value, depth + 1);
            if (block)
                lines.push(block);
            continue;
        }
        if (typeof value === "object") {
            const block = jsonObjectToNoteMarkdown(value, depth + 1);
            if (block)
                lines.push(block);
            continue;
        }
        if (typeof value === "string" && value.trim()) {
            lines.push(`- **${key}:** ${value.trim()}`);
        }
        else if (typeof value === "number" || typeof value === "boolean") {
            lines.push(`- **${key}:** ${String(value)}`);
        }
    }
    return lines.filter(Boolean).join("\n\n");
}
function jsonArrayToNoteMarkdown(items, depth = 0) {
    const lines = [];
    for (const item of items) {
        if (typeof item === "string" && item.trim()) {
            lines.push(`- ${item.trim()}`);
            continue;
        }
        if (item && typeof item === "object" && !Array.isArray(item)) {
            const block = jsonObjectToNoteMarkdown(item, depth);
            if (block)
                lines.push(block.startsWith("- ") ? block : `- ${block.replace(/\n/g, "\n  ")}`);
            continue;
        }
    }
    return lines.join("\n");
}
function jsonValueToNoteMarkdown(value) {
    if (typeof value === "string")
        return value.trim();
    if (Array.isArray(value))
        return jsonArrayToNoteMarkdown(value);
    if (value && typeof value === "object") {
        const obj = value;
        if (Array.isArray(obj.items))
            return jsonArrayToNoteMarkdown(obj.items);
        if (Array.isArray(obj.examples))
            return jsonArrayToNoteMarkdown(obj.examples);
        if (Array.isArray(obj.references))
            return jsonArrayToNoteMarkdown(obj.references);
        if (Array.isArray(obj.points))
            return jsonArrayToNoteMarkdown(obj.points);
        return jsonObjectToNoteMarkdown(obj);
    }
    return "";
}
/**
 * Turn raw agent output (often JSON-in-fences) into readable speaker-note Markdown.
 */
export function sanitizeSpeakerNoteMarkdown(raw) {
    let text = raw.trim();
    if (!text)
        return "";
    const fullFence = text.match(/^```(?:json)?\s*([\s\S]*?)```$/i);
    if (fullFence?.[1])
        text = fullFence[1].trim();
    if (text.startsWith("{") || text.startsWith("[")) {
        try {
            const parsed = JSON.parse(text);
            const md = jsonValueToNoteMarkdown(parsed);
            if (md.trim())
                return normalizeNoteMarkdown(md);
        }
        catch {
            /* fall through */
        }
    }
    text = text.replace(/```json\s*([\s\S]*?)```/gi, (_, inner) => {
        const chunk = inner.trim();
        try {
            return jsonValueToNoteMarkdown(JSON.parse(chunk));
        }
        catch {
            return chunk;
        }
    });
    text = text.replace(/```\s*([\s\S]*?)```/g, (_, inner) => inner.trim());
    return normalizeNoteMarkdown(text);
}
const NOTE_SUMMARY_MAX_WORDS = 200;
export const SPEAKER_NOTE_BODY_MAX_CHARS = 300;
export const SPEAKER_NOTE_SUMMARY_CHARS_MIN = 200;
export const SPEAKER_NOTE_SUMMARY_CHARS_MAX = 1000;
/** Hard cap on speaker note body length (characters). */
export function truncateSpeakerNoteChars(text, maxChars = SPEAKER_NOTE_BODY_MAX_CHARS) {
    const normalized = normalizeNoteMarkdown(text);
    if (normalized.length <= maxChars)
        return normalized;
    const budget = Math.max(1, maxChars - 1);
    const slice = normalized.slice(0, budget);
    const lastSpace = slice.lastIndexOf(" ");
    const cut = lastSpace > budget * 0.55 ? slice.slice(0, lastSpace) : slice;
    return `${cut.trimEnd()}…`;
}
/** Hard cap for note summary when AI summarization is unavailable. */
export function truncateSpeakerNoteMarkdown(text, maxWords = NOTE_SUMMARY_MAX_WORDS) {
    const plain = stripSpeakerScriptPlain(text);
    const words = plain.split(/\s+/).filter(Boolean);
    if (words.length <= maxWords)
        return normalizeNoteMarkdown(text);
    return `${words.slice(0, maxWords).join(" ")}…`;
}
export const SPEAKER_NOTE_SUMMARY_MAX_WORDS = NOTE_SUMMARY_MAX_WORDS;
